import type { EditorJsEditorHandle } from './editor';
import type { AiInstructionsEditRequest } from './editorjs-ai-instructions-tune';
import type { GalleryEditRequest } from './editorjs-gallery-tool';
import type { EditorJsData } from './editorjs-types';
import type { RefObject } from 'react';

import { useCallback, useState } from 'react';

import { AiInstructionsModal } from './ai-instructions-modal';
import { useDocumentChatComposer } from './document-chat-composer-context';
import { EditDocumentEditor } from './editor';
import { GalleryEditModal } from './gallery-edit-modal';
import { GalleryImageModalProvider } from './gallery-image-modal-context';

interface ViewDocumentViewerProps {
  editorRef: RefObject<EditorJsEditorHandle | null>;
  documentId?: string;
  isNewDocument: boolean;
  /** Muted body text until the user edits (new path with seeded default content). */
  editorPlaceholderMuted?: boolean;
  path: string;
  displayPath: string;
  value: EditorJsData | null;
  apiHost: string;
  userEmail: string;
  children: Array<{ id: string; path: string }>;
  onCreateSubpage: () => void;
  /** Navigate to a document path (client-side). Used by Subpages block links. */
  onNavigateToDocument?: (path: string) => void;
}

export function ViewDocumentViewer({
  editorRef,
  documentId,
  isNewDocument,
  editorPlaceholderMuted = false,
  path,
  displayPath,
  value,
  apiHost,
  userEmail,
  children,
  onCreateSubpage,
  onNavigateToDocument,
}: ViewDocumentViewerProps) {
  const { appendEmphasis, focusComposer } = useDocumentChatComposer();
  const [galleryEditRequest, setGalleryEditRequest] =
    useState<GalleryEditRequest | null>(null);
  const [aiInstructionsRequest, setAiInstructionsRequest] =
    useState<AiInstructionsEditRequest | null>(null);

  const handleRequestGalleryEdit = useCallback(
    (request: GalleryEditRequest) => {
      setGalleryEditRequest(request);
    },
    [],
  );

  const handleRequestAiInstructionsEdit = useCallback(
    (request: AiInstructionsEditRequest) => {
      setAiInstructionsRequest(request);
    },
    [],
  );

  const handleAddToChat = useCallback(
    (blockId: string) => {
      appendEmphasis(blockId);
      focusComposer();
    },
    [appendEmphasis, focusComposer],
  );

  const handleUpload = useCallback(
    async (file: File): Promise<string> => {
      const { uploadImageFile } = await import('./upload-image');
      const result = await uploadImageFile(file, {
        apiHost,
        userEmail,
        ...(documentId ? { documentId } : {}),
      });
      return result.url;
    },
    [apiHost, documentId, userEmail],
  );

  return (
    <GalleryImageModalProvider>
      <GalleryEditModal
        request={galleryEditRequest}
        onDismiss={() => setGalleryEditRequest(null)}
        onUpload={handleUpload}
      />
      <AiInstructionsModal
        request={aiInstructionsRequest}
        onDismiss={() => setAiInstructionsRequest(null)}
      />
      <EditDocumentEditor
        key={documentId ?? path}
        ref={editorRef}
        value={value}
        documentId={documentId}
        apiHost={apiHost}
        isNewDocument={isNewDocument}
        editorPlaceholderMuted={editorPlaceholderMuted}
        onUpload={handleUpload}
        parentPath={displayPath}
        children={children}
        onCreateSubpage={onCreateSubpage}
        onNavigateToDocument={onNavigateToDocument}
        onAddToChat={handleAddToChat}
        onRequestGalleryEdit={handleRequestGalleryEdit}
        onRequestAiInstructionsEdit={handleRequestAiInstructionsEdit}
      />
    </GalleryImageModalProvider>
  );
}
